import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../services/apiClient';
import config from '../../../config/env';
import { useI18n } from '../../../contexts/I18nContext';
import { useFirmwareOptions } from '../useFirmwareOptions';
import { FirmwareFormData, FirmwareFormErrors, buildFirmwarePayload, emptyFirmwareForm, explainFirmwareError, validateFirmwareForm } from '../firmwareForm';

/** What the backend extracts from an uploaded ESP32 image. */
export interface FirmwareUploadResult {
  file_url: string;
  filename: string;
  file_size: number;
  file_sha256: string;
  file_md5: string;
  is_valid_esp32: boolean;
  magic: number;
  segments: number;
  flash_mode: string;
  flash_size: string;
  flash_freq: string;
  entry_point: string;
  app_version: string | null;
  project_name: string | null;
  compile_time: string | null;
  compile_date: string | null;
  idf_version: string | null;
  elf_sha256: string | null;
  suggested_version: string | null;
  suggested_model: string | null;
}

interface Options {
  isOpen: boolean;
  onClose: () => void;
  onFirmwareCreated: () => void;
}

/**
 * The "add firmware" dialog: upload the image (the analysis prefills the
 * form), describe the firmware, submit. Closing resets everything.
 */
export const useAddFirmware = ({ isOpen, onClose, onFirmwareCreated }: Options) => {
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);
  const { models, clients } = useFirmwareOptions(isOpen);

  const [form, setForm] = useState<FirmwareFormData>(emptyFirmwareForm);
  const [errors, setErrors] = useState<FirmwareFormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<FirmwareUploadResult | null>(null);

  // The first model is preselected: the select has no empty option.
  useEffect(() => {
    if (models.length > 0 && !form.model) setForm((prev) => (prev.model ? prev : { ...prev, model: models[0].code }));
  }, [models, form.model]);

  const setField = (field: keyof FirmwareFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  /** Uploads the image; its analysis fills version, URL, size and checksum. */
  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await axios.post<FirmwareUploadResult>('/v2/firmware/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      const result = response.data;
      setUploadResult(result);
      const baseUrl = config.api.firmwareBaseUrl.replace(/\/$/, '');
      setForm((prev) => ({
        ...prev,
        version: result.suggested_version || prev.version,
        file_url: `${baseUrl}/v2/firmware/files/${result.filename}`,
        file_size: String(result.file_size),
        checksum: result.file_sha256,
      }));
    } catch (err: any) {
      console.error('[ADD FIRMWARE] Upload failed:', err);
      setError(explainFirmwareError(err, t('otaUpdates.uploadFailed')));
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setForm(emptyFirmwareForm());
    setErrors({});
    setError(null);
    setUploadResult(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const found = validateFirmwareForm(form, t);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    try {
      await axios.post('/v2/firmware', buildFirmwarePayload(form));
      reset();
      onFirmwareCreated();
      onClose();
    } catch (err: any) {
      console.error('[ADD FIRMWARE] Error:', err);
      setError(explainFirmwareError(err, t('otaUpdates.createFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  /** Closing is refused while a request is in flight. */
  const close = () => {
    if (submitting || uploading) return;
    reset();
    onClose();
  };

  return {
    form, errors, error, dismissError: () => setError(null), submitting, uploading, uploadResult, models, clients,
    setField, upload, submit, close,
  };
};

export type AddFirmwareState = ReturnType<typeof useAddFirmware>;
